import { z } from 'zod';

// Common validation schemas
export const amountSchema = z.number().positive('Valor deve ser positivo');
export const dateSchema = z.string().min(1, 'Data é obrigatória');
export const nameSchema = z.string().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo');
export const colorSchema = z.string().regex(/^#[0-9A-F]{6}$/i, 'Cor inválida');

// Updated description schema with 100 character limit
export const descriptionSchema = z.string().max(100, 'Descrição deve ter no máximo 100 caracteres').optional().or(z.literal(''));

// Transaction validation with conditional account requirement
export const transactionSchema = z.object({
  description: descriptionSchema,
  amount: amountSchema,
  categoryId: z.string().min(1, 'Categoria é obrigatória'),
  date: dateSchema,
  type: z.enum(['receita', 'despesa']),
  account: z.string().optional(), // Legacy field - real validation is in accountId refine
  accountId: z.string().optional(),
  paymentMethod: z.enum(['pix', 'dinheiro', 'cartao', 'cartao_debito', 'boleto', 'transferencia']),
  cardId: z.string().optional()
}).refine((data) => {
  // If payment method is card AND type is expense, cardId is required
  if (data.paymentMethod === 'cartao' && data.type === 'despesa') {
    return data.cardId && data.cardId.length > 0;
  }
  return true;
}, {
  message: 'Cartão é obrigatório quando meio de pagamento é cartão para despesas',
  path: ['cardId']
}).refine((data) => {
  // If payment method is NOT card and NOT transfer and NOT debit card, accountId is required
  if (data.paymentMethod !== 'cartao' && data.paymentMethod !== 'transferencia' && data.paymentMethod !== 'cartao_debito') {
    return data.accountId && data.accountId.length > 0;
  }
  // For card revenues, accountId is also required
  if (data.paymentMethod === 'cartao' && data.type === 'receita') {
    return data.accountId && data.accountId.length > 0;
  }
  // For debit card transactions (both revenue and expense), accountId is required
  if (data.paymentMethod === 'cartao_debito') {
    return data.accountId && data.accountId.length > 0;
  }
  return true;
}, {
  message: 'Conta é obrigatória',
  path: ['accountId']
});

// Category validation
export const categorySchema = z.object({
  name: nameSchema,
  type: z.enum(['receita', 'despesa']),
  color: colorSchema
});

// Card validation
export const cardSchema = z.object({
  name: nameSchema,
  bank: z.string().min(1, 'Banco é obrigatório'),
  accountId: z.string().min(1, 'Conta vinculada é obrigatória'),
  limit: amountSchema,
  dueDate: z.number().min(1).max(31, 'Dia de vencimento inválido'),
  color: colorSchema
});

// Budget validation
export const budgetSchema = z.object({
  categoryId: z.string().min(1, 'Categoria é obrigatória'),
  amount: amountSchema,
  period: z.enum(['diario', 'semanal', 'mensal', 'anual'])
});

// Debt validation
export const debtSchema = z.object({
  name: nameSchema,
  totalAmount: amountSchema,
  installments: z.number().positive('Número de parcelas deve ser positivo'),
  categoryId: z.string().min(1, 'Categoria é obrigatória'),
  dueDay: z.number().min(1).max(31, 'Dia de vencimento inválido'),
  interestRate: z.number().min(0).max(100).optional(),
  description: z.string().max(100, 'Descrição deve ter no máximo 100 caracteres').optional(),
  startDate: dateSchema
});

// Updated Goal validation with category
export const goalSchema = z.object({
  name: nameSchema,
  targetValue: amountSchema,
  deadline: dateSchema,
  color: colorSchema,
  category: z.string().min(1, 'Categoria é obrigatória')
});

// Transfer validation
export const transferSchema = z.object({
  fromAccountId: z.string().min(1, 'Conta de origem é obrigatória'),
  toAccountId: z.string().min(1, 'Conta de destino é obrigatória'),
  amount: amountSchema,
  description: z.string().max(100, 'Descrição deve ter no máximo 100 caracteres').optional(),
  date: dateSchema
}).refine((data) => {
  return data.fromAccountId !== data.toAccountId;
}, {
  message: 'Conta de origem deve ser diferente da conta de destino',
  path: ['toAccountId']
});

// Validation helper functions
export const validateForm = <T>(schema: z.ZodSchema<T>, data: unknown): { success: boolean; data?: T; errors?: Record<string, string> } => {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.errors.forEach((err) => {
        if (err.path.length > 0) {
          errors[err.path[0] as string] = err.message;
        }
      });
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'Erro de validação' } };
  }
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

export const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString('pt-BR');
};

// Email change validation
export const emailChangeSchema = z.object({
  newEmail: z.string().email('Email inválido').min(1, 'Novo email é obrigatório'),
  confirmEmail: z.string().email('Email inválido').min(1, 'Confirmação de email é obrigatória'),
  password: z.string().min(1, 'Senha atual é obrigatória')
}).refine((data) => data.newEmail === data.confirmEmail, {
  message: 'Os emails não coincidem',
  path: ['confirmEmail']
});

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};
